import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import {
    CM2Dataset,
    ControlsHubChartData,
    ControlsHubPanelObservationActions,
    ControlsHubPanelObservationReviewLogs,
    ControlsHubPanelObservations,
    ControlsHubPanelsAssociatedToRisk,
    ControlsHubWidgets,
    PanelResult,
} from '@interfaces/central-monitoring';
import { getData, postData, ValidationErrorResponse } from '@apis/rest-client';

const ControlsHubApi = {
    useGetWidgets: (studyRef: string, datasetId?: number, country?: string) => {
        return useSWR<ControlsHubWidgets[], ValidationErrorResponse>(
            studyRef && datasetId ? `CentralMonitoring/controlswidgets/${studyRef}/${datasetId}` : null,
            (url: string) => {
                const searchParams = country ? new URLSearchParams({ country }) : new URLSearchParams();
                return getData<ControlsHubWidgets[]>(url, {
                    timeout: 30000,
                    searchParams,
                });
            },
        );
    },

    useGetPanelData: (studyRef: string, datasetId?: number, panelRef?: string, country?: string, active?: boolean) => {
        const searchParams = country ? new URLSearchParams({ country }) : new URLSearchParams();

        return useSWR<PanelResult>(
            datasetId && panelRef && active
                ? `CentralMonitoring/controlshub/${studyRef}/${datasetId}/panel/${panelRef}`
                : null,
            (url: string) => {
                return getData<PanelResult>(url, {
                    timeout: 30000,
                    searchParams,
                    retry: { limit: 10, statusCodes: [503], delay: () => 1000 },
                });
            },
        );
    },

    useGetTimePeriodsForPanel: (studyRef: string, datasetId?: number, panelRef?: string) => {
        const url =
            datasetId && panelRef
                ? `CentralMonitoring/ControlsHub/${studyRef}/${datasetId}/GetTimePeriodsForPanel/${panelRef}`
                : null;

        return useSWR<string[]>(url, getData);
    },

    useGetPanelsAssociatedToRisk: (studyRef: string) => {
        const saveUrl = `CentralMonitoring/ControlsHub/${studyRef}/panelsAssociatedToRisk`;

        return useSWRMutation(saveUrl, (url: string, { arg }: { arg: string[] }) => {
            return postData<string[], ControlsHubPanelsAssociatedToRisk[]>(url, {
                arg,
                timeout: 30000,
            });
        });
    },

    useGetControlsHubChartData: (studyRef?: string, datasetId?: number, panelRef?: string) => {
        const url =
            studyRef && datasetId && panelRef
                ? `CentralMonitoring/ControlsHub/${studyRef}/${datasetId}/ChartData/${panelRef}`
                : null;

        return useSWR<ControlsHubChartData[]>(url, getData);
    },
    useGetControlsHubChartObservations: (studyRef?: string, panelRef?: string) => {
        const url =
            studyRef && panelRef ? `CentralMonitoring/Observation/Controls/Panel/${panelRef}/Grid/${studyRef}` : null;

        return useSWR<ControlsHubPanelObservations[]>(url, getData);
    },
    useGetControlsHubChartObservationReviewLogs: (studyRef?: string, observationId?: string) => {
        const url =
            studyRef && observationId ? `CentralMonitoring/ControlsReviewLog/Grid/${studyRef}/${observationId}` : null;

        return useSWR<ControlsHubPanelObservationReviewLogs[]>(url, getData);
    },
    useGetControlsHubChartObservationActions: (studyRef?: string, observationId?: string) => {
        const url =
            studyRef && observationId ? `CentralMonitoring/Actions/Controls/Grid/${studyRef}/${observationId}` : null;

        return useSWR<ControlsHubPanelObservationActions[]>(url, getData);
    },
    useGetLatestOOTValuesForPanel: (studyRef?: string, hasCMControlsPermission?: boolean | null) => {
        const url =
            studyRef && hasCMControlsPermission
                ? `CentralMonitoring/ControlsHub/${studyRef}/LatestOOTValuesForPanel`
                : null;

        return useSWR<ControlsHubPanelsAssociatedToRisk[]>(url, getData);
    },
    useGetPanelsWithAssociatedRisks: (studyRef?: string, hasCMControlsPermission?: boolean | null) => {
        const url =
            studyRef && hasCMControlsPermission
                ? `CentralMonitoring/ControlsHub/${studyRef}/PanelsWithAssociatedRisks`
                : null;

        return useSWR<ControlsHubPanelsAssociatedToRisk[]>(url, getData);
    },
    useGetActiveOrAssociatedPanels: (studyRef?: string, hasCMControlsPermission?: boolean | null) => {
        const url =
            studyRef && hasCMControlsPermission
                ? `CentralMonitoring/ControlsHub/${studyRef}/ActiveOrAssociatedPanels`
                : null;

        return useSWR<ControlsHubPanelsAssociatedToRisk[]>(url, getData);
    },
    useGetLatestActiveDataSet: (studyRef: string, hasCMControlsPermission?: boolean | null) => {
        const url = studyRef && hasCMControlsPermission ? `CentralMonitoring/dataset/latest-active/${studyRef}` : null;

        return useSWR<CM2Dataset, ValidationErrorResponse>(url, getData);
    },
};

export default ControlsHubApi;
